/**
 * The AttireRent mark: a swing tag with AR on it, hanging on its string, tilted a little.
 *
 * <p>
 * Chosen by the owner on 2026-10-14 from four original marks drawn for the product, with the
 * stitched cloth behind it on the app icon. Path data only, in one 100-unit square, so the website,
 * the phone and the generated icon files (`docs/brand/`) draw exactly the same shape.
 * </p>
 *
 * <p>
 * <strong>The letters are outlines, not text.</strong> They are traced from Playfair Display
 * SemiBold, which is under the SIL Open Font License and so may be used in a logo, so the mark
 * draws the same with or without the font installed — a favicon or a printed tag never loads it.
 * </p>
 */

/** Everything the short-string mark covers, including the string and the stroke on the tag. */
export const markViewBox = '14 -10 76 110';

/** Everything the long-string mark covers: the same width, the string running higher. */
export const markViewBoxLong = '14 -44 76 144';

/** Applied to the whole tag: the tilt, about a point low on the tag so it hangs from its string. */
export const tagTilt = 'rotate(-10 50 56)';

/** The tag's outline. Drawn filled and stroked at {@link tagStrokeWidth} to round its corners. */
export const tagBody = 'M27 37 L50 19 L73 37 V90 H27 Z';

export const tagStrokeWidth = 6;

/** The printed border on the tag's face, open at the top where the hole is. */
export const tagFrame = 'M32 44 V85 H68 V44';

/** The punched hole and the eyelet ring round it. */
export const tagHole = { cx: 50, cy: 32, r: 4, ring: 6 } as const;

/** The string, ending just above the tag — for a mark standing on its own. */
export const tagStringShort = 'M50 31 C50 18, 52 6, 55 -6';

/** The string running up out of view — for the app icon and a hero, where the tag hangs from above. */
export const tagStringLong = 'M50 31 C50 10, 53 -12, 57 -44';

/** Places {@link letters} (font units, baseline at 0) centred on the tag's face. */
export const lettersTransform = 'translate(33.35 71) scale(0.025)';

/** "AR" in Playfair Display SemiBold, as outlines in font units (1000 to the em). */
export const letters =
  'M353 -711L596 -84Q611 -48 628.5 -34.5Q646 -21 661 -20L661 0Q635 -2 598.5 -2.5Q562 -3 524 -3Q480 -3 442 -2.5Q404 -2 380 0L380 -20Q431 -22 445 -37.5Q459 -53 439 -104L263 -579L281 -601L121 -186Q102 -136 98 -104Q94 -72 103 -54Q112 -36 133 -28.5Q154 -21 185 -20L185 0Q153 -2 123 -2.5Q93 -3 63 -3Q41 -3 21.5 -2.5Q2 -2 -13 0L-13 -20Q9 -25 30.5 -46Q52 -67 72 -119L301 -711Q313 -710 327 -710Q341 -710 353 -711ZM440 -288L440 -268L142 -268L152 -288L440 -288ZM691 -708Q716 -707 755 -706Q794 -705 833 -705Q882 -705 926 -706Q970 -707 988 -707Q1078 -707 1136.5 -685.5Q1195 -664 1224 -625Q1253 -586 1253 -533Q1253 -500 1240 -465.5Q1227 -431 1195 -402Q1163 -373 1107.5 -354.5Q1052 -336 966 -336L870 -336L870 -356L956 -356Q1019 -356 1052.5 -378.5Q1086 -401 1097.5 -438.5Q1109 -476 1109 -522Q1109 -601 1078 -644.5Q1047 -688 971 -688Q930 -688 917.5 -671Q905 -654 905 -602L905 -106Q905 -70 911 -52Q917 -34 933.5 -28Q950 -22 982 -20L982 0Q957 -2 918 -2.5Q879 -3 839 -3Q794 -3 755 -2.5Q716 -2 691 0L691 -20Q724 -22 740.5 -28Q757 -34 762.5 -52Q768 -70 768 -106L768 -602Q768 -639 762.5 -656.5Q757 -674 740.5 -680.5Q724 -687 691 -688L691 -708ZM870 -353Q917 -351 948.5 -348.5Q980 -346 1002.5 -344Q1025 -342 1043 -339Q1126 -331 1165.5 -300.5Q1205 -270 1218 -208L1241 -105Q1250 -64 1260.5 -46.5Q1271 -29 1291 -30Q1305 -30 1315 -37.5Q1325 -45 1335 -58L1349 -46Q1324 -15 1296 -0.5Q1268 14 1225 14Q1177 14 1144 -8.5Q1111 -31 1099 -94L1080 -194Q1072 -236 1061.5 -268Q1051 -300 1033 -318Q1015 -336 980 -336L872 -336L870 -353Z';

/** The brand colours the mark is drawn in: Peacock's teal, the deep band, and ink for print. */
export const markColours = { teal: '#0E7490', deep: '#083344', ink: '#111111' } as const;
